import React, { useState } from 'react';
import { Flight } from '../../types/flight';
import { useLanguage } from '../../contexts/LanguageContext';
import { Edit } from 'lucide-react';
import { EditFlightModal } from './EditFlightModal';

type Props = {
  flights: Flight[];
};

export const FlightsTable = ({ flights }: Props) => {
  const { translations, direction } = useLanguage();
  const [selectedFlight, setSelectedFlight] = useState<Flight | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const handleEditClick = (flight: Flight) => {
    setSelectedFlight(flight);
    setIsEditModalOpen(true);
  };

  if (flights.length === 0) {
    return (
      <div className="bg-white shadow rounded-lg p-8 text-center">
        <p className="text-gray-500">
          {translations?.flightsTable?.noFlights || 'No flights available'}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    direction === 'rtl' ? 'text-right' : 'text-left'
                  }`}
                >
                  {translations?.flightsTable?.pnrNumber || 'PNR Number'}
                </th>
                <th
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    direction === 'rtl' ? 'text-right' : 'text-left'
                  }`}
                >
                  {translations?.flightsTable?.outbound || 'Outbound'}
                </th>
                <th
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    direction === 'rtl' ? 'text-right' : 'text-left'
                  }`}
                >
                  {translations?.flightsTable?.returnFlight || 'Return'}
                </th>
                <th
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    direction === 'rtl' ? 'text-right' : 'text-left'
                  }`}
                >
                  {translations?.flightsTable?.dates || 'Dates'}
                </th>
                <th
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    direction === 'rtl' ? 'text-right' : 'text-left'
                  }`}
                >
                  {translations?.flightsTable?.actions || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {flights.map((flight) => (
                <tr key={flight.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div className="font-medium">{flight.pnrNumber}</div>
                    <div className="text-gray-500 text-xs">{flight.contractType}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{flight.outboundFlightNumber}</div>
                    <div className="text-xs text-gray-500">
                      {flight.outboundDeparture} → {flight.outboundArrival}
                    </div>
                    <div className="text-xs text-gray-500">
                      {flight.outboundArrivalTime}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{flight.returnFlightNumber}</div>
                    <div className="text-xs text-gray-500">
                      {flight.returnDeparture} → {flight.returnArrival}
                    </div>
                    <div className="text-xs text-gray-500">
                      {flight.returnDepartureTime}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <span className="text-gray-900">{flight.arrivalDate}</span>
                      <span className="text-gray-400 mx-1">→</span>
                      <span className="text-gray-900">{flight.returnDate}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <button 
                      onClick={() => handleEditClick(flight)}
                      className="text-primary-500 hover:text-primary-700 p-1 rounded-full hover:bg-primary-50"
                    >
                      <Edit size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {selectedFlight && (
        <EditFlightModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          flight={selectedFlight}
        />
      )}
    </>
  );
};